/**
 * Human-readable error messages for the api/client.ts ApiError shape.
 *
 * Two main shapes show up in `error.body`:
 *
 * 1. FastAPI plain detail:
 *      { "detail": "Account not found" }
 *
 * 2. Clerk error pass-through (our backend wraps Clerk's 4xx via
 *    `_clerk_to_http` and forwards the original body untouched):
 *      {
 *        "detail": {
 *          "errors": [
 *            { "message": "already a member",
 *              "long_message": "user@x.com is already a member of the organization.",
 *              "code": "already_a_member_in_organization" }
 *          ],
 *          "clerk_trace_id": "5f1abf..."
 *        }
 *      }
 *
 * The formatter prefers, in order: Clerk's `long_message` (most
 * user-friendly), Clerk's `message`, FastAPI's string `detail`, and
 * finally a generic fallback that mentions the HTTP status so the
 * console-trawling case still has *something* to grep.
 */

import { ApiError } from './client';

export function formatApiError(error: unknown, fallback = 'Something went wrong'): string {
  if (!(error instanceof ApiError)) {
    if (error instanceof Error && error.message) return error.message;
    return fallback;
  }

  const clerkMessage = extractClerkMessage(error.body);
  if (clerkMessage) return clerkMessage;

  const detail = extractStringDetail(error.body);
  if (detail) return detail;

  return `${fallback} (HTTP ${error.status})`;
}

function extractClerkMessage(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const detail = (body as { detail?: unknown }).detail;
  if (!detail || typeof detail !== 'object') return null;
  const errors = (detail as { errors?: unknown }).errors;
  if (!Array.isArray(errors) || errors.length === 0) return null;
  const first = errors[0];
  if (!first || typeof first !== 'object') return null;
  const longMessage = (first as { long_message?: unknown }).long_message;
  if (typeof longMessage === 'string' && longMessage.trim()) return longMessage;
  const message = (first as { message?: unknown }).message;
  if (typeof message === 'string' && message.trim()) return message;
  return null;
}

function extractStringDetail(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const detail = (body as { detail?: unknown }).detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return null;
}
